import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { ErrorInfo, isErrorInfo } from '../../../shared/interfaces/error-info.interface';
import { RootState } from '../../../shared/redux/reducers';
import { trackPage } from '../../../shared/services/utils/tracking/tracking.service';
import { getModuleName } from '../../shared/services/reservation.service';

import {
  getOneReservations,
  addGuest,
  updateOneReservations,
  reactivateOneReservations,
  addOneComment
} from '../../shared/services/data.service';
import { ReservationDetailsUiModel } from '../../../shared/ui-models/reservation.ui-model';
import { ReservationStatus } from '../../../shared/enums/reservation-status';
import { PaymentStatus } from '../../../shared/enums/payment-status.enum';
import { GuestUiModel } from '../../../shared/ui-models/guest.ui-model';
import moment from 'moment';
import { AddGuestModal } from './components/add-guest-modal/add-guest-modal.component';
import { birdToast } from '../../../shared/components/core-ui/toast';
import { AddCommentModal } from './components/add-comment-modal/add-comment-modal.component';
import { BookOfferResponseModel } from '../../../shared/ui-models/book.ui-model';
import { SelectAvailableRoomModal } from './components/select-available-room-modal/select-available-room-modal.component';
import { BookingRequestUiModel } from '../../../shared/ui-models/booking-request.ui-model';
import { getBookingRequestDetails } from '../../../shared/services/data/booking.data';
import { UserRole } from '../../../shared/enums/user-role.enum';
import { ReservationHeader } from './components/reservation-header/reservation-header.component';
import { DatesSummaryBar } from './components/dates-summary-bar/dates-summary-bar.component';
import { CommentsModal } from './components/comments-modal/comments-modal.component';
import { HistoryModal } from './components/history-modal/history-modal.component';
import { FinancialAdjustments } from './components/financial-adjustments/financial-adjustments.component';
import { GuestSection } from './components/guest-section/guest-section.component';
import { PaymentsSection } from './components/payments-section/payments-section.component';
import { ReservationSource } from '../../../shared/enums/reservation-souce.enum';
import { ChannelManagerInfoCard } from './components/channel-manager-info-card/channel-manager-info-card.component';
import { Tabs, TabsPosition } from '../../../shared/components/group-ui/tabs/tabs.component';

import { CommentIcon, TransactionHistoryIcon } from '../../../ui/Icons';

const ReservationsEdit = (props: any) => {
  const { match, userRole, userRoles } = props;
  const [reservation, setReservation] = useState<ReservationDetailsUiModel>();
  const [bookoffer, setBookoffer] = useState<BookOfferResponseModel>();
  const [bookofferLoading, setBookofferLoading] = useState<boolean>(false);
  const [reservationStatus, setReservationStatus] = useState<string>();
  const [showAddGuestModal, setShowAddGuestModal] = useState<boolean>(false);
  const [showAddCommentModal, setShowAddCommentModal] = useState<boolean>(false);
  const [showReactivateModal, setShowReactivateModal] = useState<boolean>(false);
  const [commentValue, setCommentValue] = useState<string>('');
  const [cancellationReason, setCancellationReason] = useState<string>('');
  const [skipEmail, setSkipEmail] = useState<boolean>(false);
  const [contactInfo, setContactInfo] = useState<BookingRequestUiModel | undefined>();
  const [showCommentsModal, setShowCommentsModal] = useState<boolean>(false);
  const [showHistoryModal, setShowHistoryModal] = useState<boolean>(false);
  const [isSubmittingComment, setIsSubmittingComment] = useState<boolean>(false);

  const isPermited = (permittedList: UserRole[]) => {
    return (
      permittedList.indexOf(userRole) > -1 ||
      permittedList.some((item) => userRoles?.includes(item))
    );
  };

  const getBookingDetails = useCallback((requestId: string) => {
    getBookingRequestDetails(requestId, getModuleName()).then((requestItem) => {
      if (!isErrorInfo(requestItem)) {
        setContactInfo(requestItem);
      } else {
        birdToast.error('No available room for the selected dates');
      }
    });
  }, []);

  const getOneReservation = (id: string) => {
    return getOneReservations(id).then((response: ReservationDetailsUiModel | ErrorInfo): any => {
      if (!isErrorInfo(response)) {
        if (response && response.paymentList && response.paymentList.length > 0) {
          response = {
            ...response,
            paymentList: response.paymentList
              .filter(Boolean)
              .sort((a, b) => b.paymentAmount - a.paymentAmount)
              .sort(
                (a, b) =>
                  moment(a.dueDate).hours(0).seconds(0).milliseconds(0).unix() -
                  moment(b.dueDate).hours(0).seconds(0).milliseconds(0).unix()
              )
          };
        }
        setReservation(response);
        setReservationStatus(response.status);
        getBookingDetails(response.bookingRequestId);
      }
    });
  };

  useEffect(() => {
    trackPage('reservations-edit');
    getOneReservation(match.params.id);
  }, []);

  const handleSubmitComment = async (value: string) => {
    if (!reservation?.id) return;

    setIsSubmittingComment(true);
    try {
      const response = await addOneComment(reservation.id, value);
      if (isErrorInfo(response)) {
        birdToast.error('Failed to add message');
        return;
      }

      await getOneReservation(reservation.id);
      birdToast.success('New message added successfully.');
      // setShowCommentsModal(false);
    } finally {
      setIsSubmittingComment(false);
    }
  };

  return (
    <div className="md:p-[3rem_2rem] md:min-h-[calc(100vh-45px)] md:items-start bg-creams-100/20">
      {reservation && (
        <>
          <ReservationHeader
            data={{ reservation, reservationStatus, bookofferLoading }}
            eventHandlers={{
              onStatusChange: (newStatus) => {
                setReservationStatus(newStatus);
                setCommentValue('');
                setCancellationReason('');
                setSkipEmail(false);
              },
              onOpenAddCommentModal: () => setShowAddCommentModal(true),
              onReactivateModalOpen: (offer) => {
                setBookoffer(offer);
                setShowReactivateModal(true);
              },
              onBookofferLoadingChange: setBookofferLoading,
              onRefresh: getOneReservation
            }}
          />
          <DatesSummaryBar
            data={{
              reservation,
              contactInfo: contactInfo as BookingRequestUiModel,
              isCanEditDates: isPermited([
                UserRole.RESERVATION_MANAGER,
                UserRole.ADMIN,
                UserRole.SUPER_ADMIN
              ])
            }}
            eventHandlers={{ onRefresh: getOneReservation }}
          />

          <div className="w-full mt-6">
            <Tabs
              data={{
                tabsList: [
                  {
                    key: 'Details',
                    component: (
                      <div className="flex flex-col lg:flex-row gap-4 items-stretch">
                        {/* Left column — grows with content */}
                        <div className="flex flex-col gap-4 lg:w-[55%]">
                          <GuestSection
                            data={{ reservation }}
                            eventHandlers={{
                              onAddGuest: () => setShowAddGuestModal(true),
                              onGetOneReservation: getOneReservation
                            }}
                          />
                        </div>

                        {/* Right column — fills remaining space, stacks vertically */}
                        <div className="flex flex-col gap-4 rounded-lg lg:w-[45%] border border-gray-200 shadow-md h-fit">
                          {reservation.source === ReservationSource.CHANNEL_MANAGER && (
                            <ChannelManagerInfoCard data={{ reservation }} />
                          )}
                        </div>
                      </div>
                    )
                  },
                  {
                    key: 'Payments',
                    badge: reservation.paymentList?.filter(
                      (p) => p.status !== PaymentStatus.CANCELED
                    )?.length,
                    component: (
                      <div className="flex flex-col gap-4">
                        <FinancialAdjustments data={{ reservation }} config={{ isPermited }} />
                        <PaymentsSection
                          data={{ reservation, contactEmail: contactInfo?.guestInfo?.email || '' }}
                          config={{ isPermited, userRole, userRoles }}
                          eventHandlers={{
                            onGetOneReservation: getOneReservation
                          }}
                        />
                      </div>
                    )
                  }
                ],
                defaultTab: 'Details'
              }}
              style={{ position: TabsPosition.CENTER }}
            >
              <div className="border-l border-gray-300 flex flex-row gap-2">
                <button
                  type="button"
                  onClick={() => setShowCommentsModal(true)}
                  className="relative ml-2 flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-primary-50 hover:text-primary-700 hover:border-primary-200 transition-colors text-sm font-medium shadow-sm whitespace-nowrap"
                >
                  {reservation.messages && reservation.messages.length > 0 && (
                    <span className="absolute -top-2 -right-2 inline-flex items-center justify-center size-5 px-1 rounded-full bg-primary-600 text-white text-xs font-bold leading-none">
                      {reservation.messages.length}
                    </span>
                  )}
                  <CommentIcon />
                </button>
                <button
                  type="button"
                  onClick={() => setShowHistoryModal(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-primary-50 hover:text-primary-700 hover:border-primary-200 transition-colors text-sm font-medium shadow-sm whitespace-nowrap"
                >
                  <TransactionHistoryIcon />
                </button>
              </div>
            </Tabs>
          </div>

          <AddCommentModal
            config={{
              showModal: showAddCommentModal,
              showSkipEmail:
                reservationStatus === ReservationStatus.CHECKED_IN ||
                reservationStatus === ReservationStatus.IN_HOUSE,
              isCancellation: reservationStatus === ReservationStatus.CANCELED
            }}
            eventHandlers={{
              handelChange: (value) => {
                setCommentValue(value);
              },
              handleReasonChange: (value) => {
                setCancellationReason(value);
              },
              handelSkipEmail: (value) => {
                setSkipEmail(value);
              },
              closeHandler: () => {
                setShowAddCommentModal(false);
              },
              ChangeStatusWithComment: () => {
                const statusToUpdate = reservationStatus ? reservationStatus : reservation.status;

                setShowAddCommentModal(false);
                updateOneReservations({
                  id: reservation.id,
                  unitTypeId: reservation.unitTypeId,
                  bookingRequestId: reservation.bookingRequestId,
                  status: statusToUpdate,
                  cancellationReason:
                    statusToUpdate === ReservationStatus.CANCELED ? cancellationReason : undefined,
                  comment: commentValue ? commentValue : '',
                  cancellationComment: commentValue ? commentValue : '',
                  skipEmail: skipEmail ? skipEmail : false
                }).then(() =>
                  getOneReservation(reservation.id).then(() => setShowAddGuestModal(false))
                );
              }
            }}
          />

          <CommentsModal
            config={{ showModal: showCommentsModal, isSubmitting: isSubmittingComment }}
            data={{ commentsList: reservation.messages || [] }}
            eventHandlers={{
              onClose: () => setShowCommentsModal(false),
              onSubmit: handleSubmitComment
            }}
          />

          <HistoryModal
            config={{ showModal: showHistoryModal }}
            data={{ reservationId: reservation.id }}
            eventHandlers={{ onClose: () => setShowHistoryModal(false) }}
          />

          <SelectAvailableRoomModal
            config={{ showModal: showReactivateModal }}
            data={{ availableRooms: bookoffer?.availableRooms || [] }}
            eventHandlers={{
              submitRoomId: (value) => {
                reactivateOneReservations(reservation.id, value).then(() =>
                  getOneReservation(reservation.id).then(() => setShowReactivateModal(false))
                );
              },
              closeHandler: () => {
                setShowReactivateModal(false);
              }
            }}
          />
        </>
      )}
      <AddGuestModal
        config={{ showModal: showAddGuestModal }}
        eventHandlers={{
          closeHandler: () => {
            setShowAddGuestModal(false);
          },
          addGuestHandler: (guest) => {
            addGuest(guest)
              .then((response: GuestUiModel | ErrorInfo) => {
                if (!isErrorInfo(response)) {
                  return response;
                }
              })
              .then((newGuest: GuestUiModel | undefined) => {
                if (reservation && newGuest) {
                  let updatedReservation: Partial<ReservationDetailsUiModel> = {
                    id: reservation.id
                  };
                  if (Array.isArray(reservation.guestIds)) {
                    updatedReservation = {
                      ...updatedReservation,
                      guestIds: [...reservation.guestIds, newGuest.id || '']
                    };
                  } else {
                    updatedReservation = {
                      ...updatedReservation,
                      guestIds: [newGuest.id || '']
                    };
                  }
                  updateOneReservations(updatedReservation).then(() =>
                    getOneReservation(reservation.id).then(() => setShowAddGuestModal(false))
                  );
                }
              });
          }
        }}
      />
    </div>
  );
};

export default connect(mapStateToProps)(ReservationsEdit);
function mapStateToProps(state: RootState) {
  return {
    isAuthenticated: !!state.auth.accessToken,
    permissions: state.auth.permissions,
    userRole: state.auth.role,
    userRoles: state.auth.roles
  };
}
